import { Component, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DateUtils, getMonthDateRange } from '@app/shared/utils/date.utils';
import { ToastrService } from 'ngx-toastr';
import { yearDateFormat } from '@app/shared/constants/dateFormat';
import { CommonModule } from '@angular/common';
import { VendorService } from '@app/shared/services/vendor.service';
import { SummaryItem } from '@app/shared/interfaces/vendor.model';
import { SUMMARY_KEYS } from '@app/shared/constants/vendor-constants';

@Component({
  selector: 'app-date-range',
  standalone: true,
  imports: [
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
  ],
  templateUrl: './date-range.component.html',
  styleUrl: './date-range.component.scss',
})
export class DateRangeComponent implements OnChanges {
  @ViewChild(MatDateRangePicker) picker!: MatDateRangePicker<Date>;
  @Output() monthChanged = new EventEmitter<{month: number, year: number}>();
  private activeDateSub: any;

  /** Get the MatCalendar instance from the picker's overlay (Angular Material 18) */
  private getCalendarInstance(): any {
    const picker = this.picker as any;
    // Angular Material 18 uses _popupComponentRef, older versions use _componentRef
    const contentRef = picker?._popupComponentRef ?? picker?._componentRef;
    const calendar = contentRef?.instance?._calendar ?? null;
    return calendar;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.cardComponent === 'LEAVE_APPLICATION') {
      this.summary = this.processedSummary || [];
      setTimeout(() => this.injectSummaryIntoOverlay(), 100);
    }
    if (
      changes['publicHolidays'] || changes['paidLeaveDates'] ||
      changes['unpaidLeaveDates'] || changes['halfPaidLeaveDates'] ||
      changes['halfUnpaidLeaveDates'] || changes['billableDates']
    ) {
      this.dateClass = this.buildDateClass();
      setTimeout(() => this.applyDateClassesToDom(), 0);
    }
  }

  private applyDateClassesToDom(): void {
    const calendar = this.getCalendarInstance();
    if (!calendar) return;

    const activeDate: Date = calendar.activeDate;
    const year = activeDate.getFullYear();
    const month = activeDate.getMonth();
const leaveClasses = ['public-holiday', 'paid-leave', 'unpaid-leave', 'half-paid-leave', 'half-unpaid-leave', 'last-working-day', 'dual-half-leave', 'dual-halfpaid', 'dual-halfunpaid'];

    const cells = document.querySelectorAll<HTMLElement>(
      '.mat-datepicker-content .mat-calendar-body-cell-content'
    );

    cells.forEach((cellContent) => {
      const day = parseInt(cellContent.textContent?.trim() ?? '', 10);
      if (isNaN(day)) return;

      const date = new Date(year, month, day);
      leaveClasses.forEach(cls => cellContent.classList.remove(cls));

      const cssClass = this.dateClass(date);
      if (cssClass) cellContent.classList.add(cssClass);
    });
  }

  ngAfterViewInit() {
    if (this.picker?.openedStream) {
      this.picker.openedStream.subscribe(() => {
        setTimeout(() => {
          this.injectSummaryIntoOverlay();
          this.applyDateClassesToDom();
          const calendar = this.getCalendarInstance();
          if (!calendar) return;

          // Track last emitted month/year to avoid duplicate API calls
          let lastMonth = -1;
          let lastYear = -1;

          const emitIfChanged = (date: Date) => {
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            if (month !== lastMonth || year !== lastYear) {
              lastMonth = month;
              lastYear = year;
              this.monthChanged.emit({ month, year });
            }
          };

          // Emit on open
          emitIfChanged(calendar.activeDate);

          // Unsubscribe previous listener
          if (this.activeDateSub) {
            this.activeDateSub.unsubscribe();
          }

          // Subscribe to stateChanges (fires reliably on prev/next month nav)
          this.activeDateSub = calendar.stateChanges.subscribe(() => {
            emitIfChanged(calendar.activeDate);
            this.applyDateClassesToDom();
          });
        }, 0);
      });

      this.picker.closedStream.subscribe(() => {
        if (this.activeDateSub) {
          this.activeDateSub.unsubscribe();
          this.activeDateSub = null;
        }
      });
    }
  }
  startDate: Date | null = null;
  endDate: Date | null = null;
  sendData: any;

  @Input() data: any;
  @Input() cardComponent: string | null = '';
  @Input() dateLabel: string = '';
  @Input() startDatePlaceholder: string = '';
  @Input() endDatePlaceholder: string = '';
  @Input() maxDateRangeInYears: number = Infinity;
  @Input() publicHolidays: string[] = [];
  @Input() paidLeaveDates: string[] = [];
  @Input() unpaidLeaveDates: string[] = [];
  @Input() halfPaidLeaveDates: string[] = [];
  @Input() halfUnpaidLeaveDates: string[] = [];
  @Input() billableDates: string[] = [];
  @Input() processedSummary: SummaryItem[] = [];
  @Input() required: boolean = false;
  @Output() dateRangeSelected = new EventEmitter<any>();
  summary: SummaryItem[] = [];
  SUMMARY_KEYS = SUMMARY_KEYS;

  constructor(private readonly toasterService: ToastrService) {}

  private buildDateClass(): (date: Date) => string {
    const publicHolidays = this.publicHolidays;
    const paidLeaveDates = this.paidLeaveDates;
    const unpaidLeaveDates = this.unpaidLeaveDates;
    const halfPaidLeaveDates = this.halfPaidLeaveDates;
    const halfUnpaidLeaveDates = this.halfUnpaidLeaveDates;
    const billableDates = this.billableDates;
    return (date: Date): string => {
      const dateStr = this.formatDateToString(date);
      const halfPaidCount = halfPaidLeaveDates?.filter(d => d === dateStr).length ?? 0;
      const halfUnpaidCount = halfUnpaidLeaveDates?.filter(d => d === dateStr).length ?? 0;
      if (halfPaidCount + halfUnpaidCount >= 2) {
        if (halfPaidCount > 0 && halfUnpaidCount > 0) return 'dual-half-leave';
        if (halfPaidCount >= 2) return 'dual-halfpaid';
        return 'dual-halfunpaid';
      }
      if (publicHolidays?.includes(dateStr)) return 'public-holiday';
      if (paidLeaveDates?.includes(dateStr)) return 'paid-leave';
      if (unpaidLeaveDates?.includes(dateStr)) return 'unpaid-leave';
      if (halfPaidLeaveDates?.includes(dateStr)) return 'half-paid-leave';
      if (halfUnpaidLeaveDates?.includes(dateStr)) return 'half-unpaid-leave';
      if (billableDates?.includes(dateStr)) return 'last-working-day';
      return '';
    };
  }

  dateClass: (date: Date) => string = this.buildDateClass();

  private formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  ngOnInit(){
    if(this.cardComponent != 'RTO_DETAILS' && this.cardComponent != 'LEAVE_APPLICATION'){ 
      const { startDate, endDate } = getMonthDateRange(history.state.selectedMonth, yearDateFormat.date);
      this.startDate = new Date(startDate);
      this.endDate = new Date(endDate);
    } else if (this.cardComponent === 'LEAVE_APPLICATION') {
      this.summary = this.processedSummary || [];
    }
  }

  onDateChange(): void {
    if(this.cardComponent === 'LEAVE_APPLICATION' ){
      if(this.endDate == null ){
        this.endDate = this.startDate;
      }
    }
    if(this.startDate == null || this.endDate == null){
      this.toasterService.error('Please select a valid date range.');
      return;
    }

    if (this.maxDateRangeInYears !== Infinity) {
      const daysDifference = Math.floor(
        (this.endDate.getTime() - this.startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const yearsDifference = daysDifference / 365;
      
      if (yearsDifference > this.maxDateRangeInYears) {
        this.toasterService.error(
          `Date range cannot exceed ${this.maxDateRangeInYears} year(s).`
        );
        return;
      }
    }

    const formattedStartDate = DateUtils.formatDate(this.startDate);
    const formattedEndDate = DateUtils.formatDate(this.endDate);
    this.dateRangeSelected.emit({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });
  }

  onCancel(): void {
     if(this.cardComponent === 'LEAVE_APPLICATION'){
      this.dateRangeSelected.emit({
      startDate: [],
      endDate: [],
    });
    return;
     }
    const currentMonthYear = `${new Date().getMonth() + 1},${new Date().getFullYear()}`;
    const { startDate, endDate } = getMonthDateRange(currentMonthYear, yearDateFormat.date);
    this.startDate = new Date(startDate);
    this.endDate = new Date(endDate);
    const formattedStartDate = DateUtils.formatDate(this.startDate);
    const formattedEndDate = DateUtils.formatDate(this.endDate);
    this.dateRangeSelected.emit({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });
  }

  private injectSummaryIntoOverlay(): void {
    const overlay = document.querySelector('.mat-datepicker-content');
    if (!overlay) {
      return;
    }
    
    if (overlay.querySelector('.injected-summary')) {
      overlay.querySelector('.injected-summary')?.remove();
    }
    
    if (this.cardComponent !== 'LEAVE_APPLICATION') {
      return;
    }
    
    const calendarElement = overlay.querySelector('.mat-calendar');
    if (!calendarElement) {
      return;
    }
    let wrapper = overlay.querySelector('.calendar-wrapper') as HTMLElement;
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'calendar-wrapper';
      const parent = calendarElement.parentNode;
      if (parent) {
        parent.insertBefore(wrapper, calendarElement);
        wrapper.appendChild(calendarElement);
      }
    }

    const summaryData = this.summary.length > 0 ? this.summary : [
      { text: 'Paid Leaves', value: 0 },
      { text: 'Unpaid Leaves', value: 0 },
      { text: 'Total Leaves', value: 0 }
    ];

    const summaryDiv = document.createElement('div');
    summaryDiv.className = 'summary injected-summary';

    summaryData.forEach((item, index) => {
      const itemDiv = document.createElement('div');
      itemDiv.className = 'summary-item';
      if (index === summaryData.length - 1) {
        itemDiv.classList.add('last-item');
      }

      const textDiv = document.createElement('div');
      textDiv.className = 'summary-text';
      textDiv.textContent = item.text;

      const valueDiv = document.createElement('div');
      valueDiv.className = 'summary-value';
      valueDiv.textContent = item.value.toString();

      itemDiv.appendChild(textDiv);
      itemDiv.appendChild(valueDiv);
      summaryDiv.appendChild(itemDiv);
    });

    wrapper.appendChild(summaryDiv);
  }
}